import { FREE_SERVICE_LABEL, NOT_OFFERED_LABEL } from '@constants/pricing';
import { Link } from '@tkww/orion-web-ds-action';
import { Icon, type IconName } from '@tkww/orion-web-ds-graphic';
import { TextBody } from '@tkww/orion-web-ds-text';
import React from 'react';
import type { CategoryCode } from 'types/Category';
import { useTrackCrossCategoryLinkClick } from '../../hooks/useTrackCrossCategoryLinkClick';
import { LocalAverageTooltip } from '../LocalAverageTooltip';
import Styles from './styles.scss';

export type CostBreakdownItemCrossCategoryLink = {
	label: string;
	destinationUrl: string;
	categoryCode: CategoryCode;
};

interface CostBreakdownItemProps {
	iconName: IconName;
	title: string;
	cost?: string;
	isLocalAverage?: boolean;
	isStartingPrice?: boolean;
	isFree?: boolean;
	isUnavailable?: boolean;
	crossCategoryLink?: CostBreakdownItemCrossCategoryLink;
}

const CrossCategoryLink = ({
	link,
}: {
	link: CostBreakdownItemCrossCategoryLink;
}) => {
	const trackCrossCategoryLinkClick = useTrackCrossCategoryLinkClick();

	return (
		<Link
			href={link.destinationUrl}
			onClick={() => trackCrossCategoryLinkClick(link.categoryCode)}
			className={Styles.crossCategoryLink}
			size="md"
			styleType="secondary"
			iconName="magnifier"
			labelDirection="right"
		>
			{link.label}
		</Link>
	);
};

export const CostBreakdownItem = ({
	iconName,
	title,
	cost,
	isLocalAverage,
	isStartingPrice,
	isFree,
	isUnavailable,
	crossCategoryLink,
}: CostBreakdownItemProps) => {
	return (
		<div className={Styles.itemContainer}>
			<div className={Styles.iconContainer}>
				<Icon size="md" name={iconName} aria-label={title} />
			</div>
			<div className={Styles.itemContent}>
				<div className={Styles.itemDescription}>
					<TextBody size="md" bold>
						{title}
					</TextBody>
				</div>
				<div className={Styles.itemCost}>
					{isUnavailable ? (
						<>
							<TextBody size="md">{NOT_OFFERED_LABEL}</TextBody>
							{crossCategoryLink && (
								<CrossCategoryLink link={crossCategoryLink} />
							)}
						</>
					) : (
						<>
							{isFree ? (
								<TextBody size="md">{FREE_SERVICE_LABEL}</TextBody>
							) : (
								<TextBody size="md" bold>
									{cost}
								</TextBody>
							)}
							{isLocalAverage && <LocalAverageTooltip />}
							{isStartingPrice && (
								<TextBody size="xs" asTyped color="subtle-dark">
									Starting price
								</TextBody>
							)}
						</>
					)}
				</div>
			</div>
		</div>
	);
};
